import type { ShopItem } from '@shared/types/reward.js';

import { ACCESSORY_MIRRORED } from './accessoryFit.js';
import { shopArtworkOf } from './shopArtworkManifest.js';

export function ShopArtwork({
  item,
  className,
}: {
  item: Pick<ShopItem, 'id' | 'icon'>;
  className?: string;
}) {
  const src = shopArtworkOf(item);
  if (!src) return <span aria-hidden="true">{item.icon}</span>;

  return (
    <>
      <img
        src={src}
        alt=""
        draggable={false}
        className={className}
        onError={(event) => {
          event.currentTarget.hidden = true;
          event.currentTarget.nextElementSibling?.removeAttribute('hidden');
        }}
      />
      <span hidden aria-hidden="true">
        {item.icon}
      </span>
    </>
  );
}

/**
 * Ảnh của một món — **vẽ ĐỦ CẶP** nếu món đó là loại "một ảnh = một chiếc" (`ACCESSORY_MIRRORED`).
 *
 * ⭐ VÌ SAO CẦN (lỗi chủ dự án báo: *"đôi giày sao lại thành ra như thế"*):
 *   `acc-shoes.webp` đã được vẽ lại thành **MỘT CHIẾC** để mặc lên hai bàn chân (xem
 *   `ACCESSORY_MIRRORED`). Trên người con vật, `PetAvatar` vẽ hai lần và lật gương — đúng. Nhưng
 *   mọi chỗ khác (vật thể tự do dưới sàn, khay đồ, thẻ cửa hàng) vẫn gọi `ShopArtwork` một lần
 *   ⇒ bé thấy **một chiếc giày lẻ** nằm trên sàn. Lỗi không nằm ở ảnh, mà ở chỗ thiếu bản thứ hai.
 *
 * ⚠️ LUẬT "MỘT MÓN MỘT CHỖ" KHÔNG BỊ VI PHẠM: đây là HAI BẢN VẼ của CÙNG một món trong CÙNG một
 *    ô — không thêm placement, không thêm dòng inventory. `data-artwork-pair` đánh dấu đúng ô đó
 *    để test đếm được "một món, hai bản vẽ".
 *
 * ⚠️ `PetAvatar` PHẢI dùng `ShopArtwork` (số ít), không dùng component này: ở đó mỗi bàn chân đã
 *    là một placement riêng do `accessoryLayoutFor` tính, vẽ cặp thêm lần nữa sẽ ra BỐN chiếc.
 *
 * ⚠️ Mỗi bản chiếm NỬA bề rộng ô: hai chiếc cạnh nhau đúng như một đôi giày thật. Cho cả hai ăn
 *    trọn bề rộng thì chúng chồng khít lên nhau và trông vẫn là một chiếc.
 */
export function ShopArtworkPaired({
  item,
  className,
}: {
  item: Pick<ShopItem, 'id' | 'icon'>;
  className?: string;
}) {
  if (ACCESSORY_MIRRORED[item.id] !== true) return <ShopArtwork item={item} className={className} />;

  return (
    <span
      aria-hidden="true"
      data-artwork-pair={item.id}
      className={`flex items-center justify-center ${className ?? ''}`}
    >
      <span className="block h-full w-1/2">
        <ShopArtwork item={item} className="h-full w-full object-contain" />
      </span>
      {/*
        ⚠️ `transform` đứng MỘT MÌNH trên lớp này (không lớp nào khác ở đây dùng `transform`) —
        đúng bài học đã trả giá trong `PetAvatar`: trộn `scaleX` với `translate` ở cùng một lớp
        thì phép canh tâm bị đè và chiếc giày lệch hẳn sang một bên.
      */}
      <span className="block h-full w-1/2" style={{ transform: 'scaleX(-1)' }}>
        <ShopArtwork item={item} className="h-full w-full object-contain" />
      </span>
    </span>
  );
}
